import { COMPANY } from "../data";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { LOGO_URL } from "../config";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { FooterInternationalHelp } from "./FooterInternationalHelp";
import { useLang } from "../i18n";

const KVKK_POLICY_PDF = "/documents/kisisel-verileri-saklama-ve-imha-politikasi.pdf";
const MAPS_QUERY = encodeURIComponent(COMPANY.address);
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;
const MAPS_EMBED = `https://maps.google.com/maps?q=${MAPS_QUERY}&z=15&output=embed`;

export const Footer = () => {
  const { t } = useLang();
  const [mapActive, setMapActive] = useState(false);
  return (
    <footer data-testid="footer" className="mt-12 bg-white border-t-4 border-[#2F4A26]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Marka */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="İnter Orman Ürünleri logo" className="h-11 w-auto" />
              <span className="font-serif text-2xl text-[#1A1715]">{COMPANY.name}</span>
            </div>
            <p className="text-[#6B6560] text-sm leading-relaxed max-w-xs">{t("footer.tagline")} — {COMPANY.established}</p>
            <Link
              data-testid="footer-link-sertifikalar"
              to="/sertifikalar"
              className="inline-block mt-6 font-sans uppercase tracking-[0.2em] text-[11px] text-[#2F4A26] border-b border-[#2F4A26]/40 pb-1 hover:border-[#2F4A26] transition-colors"
            >
              {t("footer.certificates")}
            </Link>
          </div>

          {/* İletişim */}
          <div className="lg:col-span-3">
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mb-4 flex items-center gap-2"><Phone size={13} strokeWidth={1.75} /> Telefon</p>
            <div className="space-y-1.5 text-sm text-[#1A1715]">
              {COMPANY.phone.filter((p) => p !== COMPANY.internationalHelp.phone).map((p, i) => (
                <a key={i} data-testid={`footer-phone-${i}`} href={`tel:${p.replace(/\s/g, "")}`} className="block hover:text-[#2F4A26] transition-colors whitespace-nowrap">{p}</a>
              ))}
            </div>
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mt-6 mb-3 flex items-center gap-2"><WhatsAppIcon size={13} /> WhatsApp</p>
            <div className="space-y-1.5 text-sm text-[#1A1715]">
              {COMPANY.whatsapp.filter((p) => p !== COMPANY.internationalHelp.whatsapp).map((p, i) => (
                <a key={i} data-testid={`footer-whatsapp-${i}`} href={`https://wa.me/${p.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="block hover:text-[#25D366] transition-colors whitespace-nowrap">{p}</a>
              ))}
            </div>
            <FooterInternationalHelp {...COMPANY.internationalHelp} />
          </div>

          {/* E-posta + Adres */}
          <div className="lg:col-span-2">
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mb-4 flex items-center gap-2"><Mail size={13} strokeWidth={1.75} /> E-Posta</p>
            <a data-testid="footer-email" href={`mailto:${COMPANY.email}`} className="text-sm text-[#1A1715] hover:text-[#2F4A26] transition-colors break-all">{COMPANY.email}</a>
            <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-[#2F4A26] mt-6 mb-3 flex items-center gap-2"><MapPin size={13} strokeWidth={1.75} /> Adres</p>
            <a data-testid="footer-address" href={MAPS_LINK} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1A1715] hover:text-[#2F4A26] transition-colors leading-relaxed">
              {COMPANY.address}
            </a>
          </div>

          {/* Harita */}
          <div className="lg:col-span-3">
            <div className="relative rounded-lg overflow-hidden ring-1 ring-black/10 h-44">
              <iframe
                title="İnter Orman Ürünleri konumu"
                data-testid="footer-map"
                src={MAPS_EMBED}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)", pointerEvents: mapActive ? "auto" : "none" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              {!mapActive && (
                <button
                  type="button"
                  data-testid="footer-map-activate"
                  onClick={() => setMapActive(true)}
                  aria-label="Haritayı etkinleştir"
                  className="absolute inset-0 w-full h-full cursor-pointer"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Alt bar — siyah */}
      <div className="bg-[#0D0C0B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-[11px] text-[#F2EFEB]/80">
          <span>© {new Date().getFullYear()} {COMPANY.name}. {t("footer.rights")}</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2 items-center">
            <a data-testid="footer-kvkk-policy-link" href={KVKK_POLICY_PDF} target="_blank" rel="noreferrer" className="hover:text-[#F2EFEB] transition-colors">
              {t("footer.kvkk")}
            </a>
            <Link data-testid="footer-yonetim-link" to="/yonetim" className="hover:text-[#F2EFEB] transition-colors">{t("footer.admin")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
